import { useEffect, useState } from 'react'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem,
  Alert, CircularProgress, IconButton, Autocomplete, Box, Typography, Chip, Divider,
  Table, TableHead, TableRow, TableCell, TableBody, Checkbox,
} from '@mui/material'
import { Add, DeleteOutlined } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getInvoice, updateInvoice, issueInvoice, markInvoicePaid, stornoInvoice, creditNoteInvoice,
  getInvoicePdf, sendInvoiceEmail, type InvoiceKind, type InvoiceStatus, type UpdateInvoicePayload,
} from '../../api/financiar'
import { listArticles } from '../../api/nomenclatoare'
import { listVatRates } from '../../api/taxe'
import { errorMessage } from '../../api/client'

const KIND_LABEL: Record<InvoiceKind, string> = { Proforma: 'Proformă', Fiscala: 'Fiscală', NotaCredit: 'Notă de credit' }
const STATUS_LABEL: Record<InvoiceStatus, string> = { Draft: 'Ciornă', Emisa: 'Emisă', Stornata: 'Stornată' }
const STATUS_COLOR: Record<InvoiceStatus, 'default' | 'success' | 'error'> = { Draft: 'default', Emisa: 'success', Stornata: 'error' }

const emptyLine = { articleId: '', quantityThousands: '', unitPricePer1000: '', discountPercent: '0', vatRateId: null as string | null, lotNumber: '' }

const lineSchema = z.object({
  articleId: z.string().min(1, 'Selectați un articol'),
  quantityThousands: z.string().min(1, 'Câmp obligatoriu'),
  unitPricePer1000: z.string().min(1, 'Câmp obligatoriu'),
  discountPercent: z.string(),
  vatRateId: z.string().nullable(),
  lotNumber: z.string(),
})
const schema = z.object({
  issueDate: z.string().min(1, 'Câmp obligatoriu'),
  dueDate: z.string().min(1, 'Câmp obligatoriu'),
  exchangeRate: z.string().min(1, 'Câmp obligatoriu'),
  lines: z.array(lineSchema).min(1, 'Adăugați cel puțin o linie'),
})
type FormValues = z.infer<typeof schema>

export default function InvoiceDetailDialog({ id, onClose }: { id: string | null; onClose: () => void }) {
  const open = id !== null
  const qc = useQueryClient()
  const [stornoMode, setStornoMode] = useState(false);
  const [stornoReason, setStornoReason] = useState('')
  const [creditMode, setCreditMode] = useState(false)
  const [creditReason, setCreditReason] = useState('')
  const [creditQuantities, setCreditQuantities] = useState<Record<string, string>>({})
  const [pendingMessage, setPendingMessage] = useState<string | null>(null)

  const { data: detail } = useQuery({ queryKey: ['financiar', 'factura', id], queryFn: () => getInvoice(id!), enabled: open })
  const { data: articles } = useQuery({ queryKey: ['nomenclatoare', 'articole', 'toate'], queryFn: () => listArticles(), enabled: open })
  const { data: vatRates } = useQuery({ queryKey: ['financiar', 'taxe', 'cote-tva'], queryFn: listVatRates, enabled: open })

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({
    defaultValues: { issueDate: '', dueDate: '', exchangeRate: '1', lines: [] },
    resolver: zodResolver(schema),
  })
  const lines = useFieldArray({ control, name: 'lines' })

  useEffect(() => {
    if (detail) {
      reset({
        issueDate: detail.issueDate, dueDate: detail.dueDate, exchangeRate: String(detail.exchangeRate),
        lines: detail.lines.map((l) => ({
          articleId: l.articleId, quantityThousands: String(l.quantityThousands), unitPricePer1000: String(l.unitPricePer1000),
          discountPercent: String(l.discountPercent), vatRateId: l.vatRateId, lotNumber: l.lotNumber ?? '',
        })),
      })
    }
    setStornoMode(false); setStornoReason(''); setCreditMode(false); setCreditReason(''); setCreditQuantities({}); setPendingMessage(null)
  }, [detail, reset])

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['financiar', 'facturi'] })
    void qc.invalidateQueries({ queryKey: ['financiar', 'factura', id] })
  }

  const saveMutation = useMutation({
    mutationFn: (values: FormValues) => {
      const payload: UpdateInvoicePayload = {
        issueDate: values.issueDate, dueDate: values.dueDate, exchangeRate: Number(values.exchangeRate),
        lines: values.lines.map((l) => ({
          articleId: l.articleId, quantityThousands: Number(l.quantityThousands), unitPricePer1000: Number(l.unitPricePer1000),
          discountPercent: Number(l.discountPercent), vatRateId: l.vatRateId, lotNumber: l.lotNumber || null, productionOrderId: null,
        })),
      }
      return updateInvoice(id!, payload)
    },
    onSuccess: invalidate,
  })
  const issueMutation = useMutation({ mutationFn: () => issueInvoice(id!), onSuccess: invalidate })
  const markPaidMutation = useMutation({ mutationFn: () => markInvoicePaid(id!), onSuccess: invalidate })
  const stornoMutation = useMutation({
    mutationFn: () => stornoInvoice(id!, stornoReason),
    onSuccess: (r) => { invalidate(); setStornoMode(false); if (r.pendingApproval) setPendingMessage('Stornarea a fost trimisă la aprobare.') },
  })
  const creditMutation = useMutation({
    mutationFn: () => creditNoteInvoice(id!, creditReason,
      Object.entries(creditQuantities).filter(([, q]) => Number(q) > 0).map(([originalLineId, q]) => ({ originalLineId, quantityThousands: Number(q) }))),
    onSuccess: (r) => { invalidate(); setCreditMode(false); if (r.pendingApproval) setPendingMessage('Nota de credit a fost trimisă la aprobare.') },
  })
  const sendEmailMutation = useMutation({ mutationFn: () => sendInvoiceEmail(id!) })

  const handleDownloadPdf = async () => {
    const blob = await getInvoicePdf(id!)
    const url = URL.createObjectURL(blob)
    window.open(url, '_blank')
  }

  if (!detail) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent><Stack sx={{ py: 4, alignItems: 'center' }}><CircularProgress /></Stack></DialogContent>
      </Dialog>
    )
  }

  const isDraft = detail.status === 'Draft'
  const isEmisaFiscala = detail.status === 'Emisa' && detail.kind === 'Fiscala' && !detail.isStorno
  const anyMutationError = saveMutation.error ?? issueMutation.error ?? stornoMutation.error ?? creditMutation.error ?? sendEmailMutation.error ?? markPaidMutation.error

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {KIND_LABEL[detail.kind]} {detail.number}{' '}
        <Chip size="small" label={STATUS_LABEL[detail.status]} color={STATUS_COLOR[detail.status]} variant="outlined" sx={{ ml: 1 }} />
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Client: {detail.clientName} · Comandă: {detail.orderNumber ?? '—'} · Expediție: {detail.shipmentNumber ?? '—'}
            {detail.relatedInvoiceNumber && <> · Document aferent: {detail.relatedInvoiceNumber}</>}
          </Typography>

          {isDraft ? (
            <Stack direction="row" spacing={2}>
              <Controller name="issueDate" control={control} render={({ field }) => (
                <TextField {...field} type="date" label="Dată emitere" sx={{ width: 170 }} slotProps={{ inputLabel: { shrink: true } }}
                  error={!!errors.issueDate} helperText={errors.issueDate?.message} />
              )} />
              <Controller name="dueDate" control={control} render={({ field }) => (
                <TextField {...field} type="date" label="Scadență" sx={{ width: 170 }} slotProps={{ inputLabel: { shrink: true } }}
                  error={!!errors.dueDate} helperText={errors.dueDate?.message} />
              )} />
              {detail.currency !== 'RON' && (
                <Controller name="exchangeRate" control={control} render={({ field }) => (
                  <TextField {...field} type="number" label={`Curs ${detail.currency}/RON`} sx={{ width: 170 }}
                    error={!!errors.exchangeRate} helperText={errors.exchangeRate?.message} />
                )} />
              )}
            </Stack>
          ) : (
            <Typography variant="body2">
              Emisă: {detail.issueDate} · Scadență: {detail.dueDate} · Monedă: {detail.currency}
              {detail.currency !== 'RON' && ` (curs ${detail.exchangeRate})`}
              {detail.paidAt && <> · Plătită la {detail.paidAt}</>}
            </Typography>
          )}

          <Divider />

          {isDraft ? (
            <>
              {lines.fields.map((f, i) => (
                <Stack key={f.id} direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                  <Controller name={`lines.${i}.articleId`} control={control} render={({ field }) => (
                    <Autocomplete
                      sx={{ flexGrow: 1.2 }} options={articles?.map((a) => a.id) ?? []}
                      getOptionLabel={(aid) => { const a = articles?.find((x) => x.id === aid); return a ? `${a.code} — ${a.shortDescription}` : aid }}
                      value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                      renderInput={(params) => (
                        <TextField {...params} label="Articol" error={!!errors.lines?.[i]?.articleId} helperText={errors.lines?.[i]?.articleId?.message} />
                      )}
                    />
                  )} />
                  <Controller name={`lines.${i}.quantityThousands`} control={control} render={({ field }) => (
                    <TextField {...field} type="number" label="Cant. (mii)" sx={{ width: 110 }}
                      error={!!errors.lines?.[i]?.quantityThousands} helperText={errors.lines?.[i]?.quantityThousands?.message} />
                  )} />
                  <Controller name={`lines.${i}.unitPricePer1000`} control={control} render={({ field }) => (
                    <TextField {...field} type="number" label="Preț/1000" sx={{ width: 120 }}
                      error={!!errors.lines?.[i]?.unitPricePer1000} helperText={errors.lines?.[i]?.unitPricePer1000?.message} />
                  )} />
                  <Controller name={`lines.${i}.discountPercent`} control={control} render={({ field }) => (
                    <TextField {...field} type="number" label="Discount %" sx={{ width: 100 }} />
                  )} />
                  <Controller name={`lines.${i}.vatRateId`} control={control} render={({ field }) => (
                    <TextField {...field} value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value || null)} select label="TVA" sx={{ width: 110 }}>
                      <MenuItem value="">—</MenuItem>
                      {vatRates?.map((v) => <MenuItem key={v.id} value={v.id}>{v.code} ({v.ratePercent}%)</MenuItem>)}
                    </TextField>
                  )} />
                  <Controller name={`lines.${i}.lotNumber`} control={control} render={({ field }) => (
                    <TextField {...field} label="Lot" sx={{ width: 120 }} />
                  )} />
                  <IconButton onClick={() => lines.remove(i)} sx={{ mt: 0.5 }}><DeleteOutlined fontSize="small" /></IconButton>
                </Stack>
              ))}
              <Box>
                <Button size="small" startIcon={<Add />} onClick={() => lines.append(emptyLine)}>Adaugă linie</Button>
              </Box>
              {typeof errors.lines?.message === 'string' && <Alert severity="warning">{errors.lines.message}</Alert>}
            </>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  {creditMode && <TableCell />}
                  <TableCell>Articol</TableCell><TableCell>Lot</TableCell>
                  <TableCell align="right">Cantitate</TableCell><TableCell align="right">Preț/1000</TableCell>
                  <TableCell align="right">TVA %</TableCell><TableCell align="right">Fără TVA</TableCell><TableCell align="right">Total</TableCell>
                  {creditMode && <TableCell align="right">Cant. de creditat</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {detail.lines.map((l) => (
                  <TableRow key={l.id}>
                    {creditMode && (
                      <TableCell padding="checkbox">
                        <Checkbox checked={!!creditQuantities[l.id]}
                          onChange={(e) => setCreditQuantities((q) => ({ ...q, [l.id]: e.target.checked ? String(l.quantityThousands) : '' }))} />
                      </TableCell>
                    )}
                    <TableCell>{l.articleCode} — {l.description}</TableCell>
                    <TableCell>{l.lotNumber ?? '—'}</TableCell>
                    <TableCell align="right">{l.quantityThousands}</TableCell>
                    <TableCell align="right">{l.unitPricePer1000.toFixed(4).replace('.', ',')}</TableCell>
                    <TableCell align="right">{l.vatRatePercent}</TableCell>
                    <TableCell align="right">{l.netAmount.toFixed(2).replace('.', ',')}</TableCell>
                    <TableCell align="right">{l.totalAmount.toFixed(2).replace('.', ',')}</TableCell>
                    {creditMode && (
                      <TableCell align="right">
                        <TextField type="number" size="small" sx={{ width: 100 }} disabled={!creditQuantities[l.id]}
                          value={creditQuantities[l.id] ?? ''} onChange={(e) => setCreditQuantities((q) => ({ ...q, [l.id]: e.target.value }))} />
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Typography variant="body2" sx={{ alignSelf: 'flex-end' }}>
            Fără TVA: {detail.netTotal.toFixed(2).replace('.', ',')} · TVA: {detail.vatTotal.toFixed(2).replace('.', ',')} · <b>Total: {detail.total.toFixed(2).replace('.', ',')} {detail.currency}</b>
          </Typography>

          {stornoMode && (
            <Alert severity="warning">
              <Stack spacing={1}>
                <Typography variant="body2">Stornarea anulează integral factura și generează un document de stornare.</Typography>
                <TextField label="Motiv stornare" fullWidth multiline minRows={2} value={stornoReason} onChange={(e) => setStornoReason(e.target.value)} />
              </Stack>
            </Alert>
          )}
          {creditMode && (
            <Alert severity="info">
              <Stack spacing={1}>
                <Typography variant="body2">Selectați liniile și cantitatea de creditat (retur parțial).</Typography>
                <TextField label="Motiv notă de credit" fullWidth multiline minRows={2} value={creditReason} onChange={(e) => setCreditReason(e.target.value)} />
              </Stack>
            </Alert>
          )}
          {pendingMessage && <Alert severity="info">{pendingMessage}</Alert>}
          {isDraft && Object.keys(errors).length > 0 && (
            <Alert severity="warning">Există câmpuri obligatorii necompletate — verificați câmpurile marcate cu roșu mai sus.</Alert>
          )}
          {anyMutationError && <Alert severity="error">{errorMessage(anyMutationError)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, flexWrap: 'wrap' }}>
        <Button onClick={onClose}>Închide</Button>
        {isDraft && (
          <>
            <Button onClick={handleSubmit((v) => saveMutation.mutate(v))} disabled={saveMutation.isPending}>Salvează modificările</Button>
            <Button variant="contained" onClick={() => issueMutation.mutate()} disabled={issueMutation.isPending}>Emite factura</Button>
          </>
        )}
        {(detail.status === 'Emisa') && (
          <>
            <Button onClick={handleDownloadPdf}>Descarcă PDF</Button>
            <Button onClick={() => sendEmailMutation.mutate()} disabled={sendEmailMutation.isPending}>Trimite email</Button>
          </>
        )}
        {isEmisaFiscala && !detail.paidAt && (
          <Button onClick={() => markPaidMutation.mutate()} disabled={markPaidMutation.isPending}>Marchează plătită</Button>
        )}
        {isEmisaFiscala && !stornoMode && !creditMode && (
          <>
            <Button color="warning" onClick={() => setCreditMode(true)}>Notă de credit</Button>
            <Button color="error" onClick={() => setStornoMode(true)}>Stornează</Button>
          </>
        )}
        {stornoMode && (
          <>
            <Button onClick={() => setStornoMode(false)}>Renunță</Button>
            <Button color="error" variant="contained" disabled={!stornoReason.trim() || stornoMutation.isPending} onClick={() => stornoMutation.mutate()}>
              Confirmă stornarea
            </Button>
          </>
        )}
        {creditMode && (
          <>
            <Button onClick={() => setCreditMode(false)}>Renunță</Button>
            <Button color="warning" variant="contained"
              disabled={!creditReason.trim() || !Object.values(creditQuantities).some((q) => Number(q) > 0) || creditMutation.isPending}
              onClick={() => creditMutation.mutate()}>
              Confirmă nota de credit
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
